import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CircularProgress, Stack } from "@mui/material";
import { useTransactionsDetail } from "Api/Queries/Transactions/useTransactionsDetail";
import { NfcPrompt } from "Components/Customer/SendPayment/NfcPrompt";
import {
  type StatusAlertKind,
  TransactionStatusAlert,
} from "Components/Shared/Payments/TransactionStatusAlert";
import { TransactionIdEntryForm } from "Components/Shared/Payments/TransactionIdEntryForm";
import {
  type TransactionIdEntryFormData,
  useTransactionIdEntryForm,
} from "Components/Shared/Payments/useTransactionIdEntryForm";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { useIsPhone } from "Hooks/useIsPhone";
import { TransactionStatus } from "Infrastructure/Api/Api";
import { useNavigate } from "Navigation/useNavigate";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";
import type { FieldErrors } from "react-hook-form";

const PageResources = Resources.Customer.Pay.ManualEntry;
const ErrorResources = Resources.TransactionErrors;

const STATUS_TO_ALERT: Partial<Record<TransactionStatus, StatusAlertKind>> = {
  [TransactionStatus.Success]: "paid",
  [TransactionStatus.Cancelled]: "cancelled",
};

type Props = {
  publicID?: string | null;
};

export const SendPaymentPageContent = ({ publicID }: Props) => {
  const { t } = useResource();
  const navigate = useNavigate();
  const isPhone = useIsPhone();

  const {
    control,
    handleSubmit,
    transactionPublicID,
    hasExactLength,
    isTooLong,
  } = useTransactionIdEntryForm();

  // Once the ID is exactly the expected length, fetch (and cache) the
  // transaction so we can surface errors here, and the pay screen reads it
  // instantly from the cache (same query key).
  const {
    data: transactionDetails,
    isLoading: isLoadingTransaction,
    isError: isTransactionError,
  } = useTransactionsDetail({
    publicID: hasExactLength ? transactionPublicID : undefined,
  });

  const statusAlertKind = transactionDetails
    ? STATUS_TO_ALERT[transactionDetails.transactionStatus]
    : undefined;
  const isTransactionUnavailable =
    isTransactionError || statusAlertKind !== undefined;
  const isInvalid = isTooLong || isTransactionUnavailable;
  const isSubmitDisabled =
    !hasExactLength || isLoadingTransaction || isTransactionUnavailable;

  // Surface ID-level problems (over-length, or not found) as the field error
  // — red border + message under the input — instead of a separate alert.
  const getFieldErrorMessage = () => {
    if (isTooLong) {
      return t(PageResources.InvalidId);
    }
    if (isTransactionError) {
      return t(ErrorResources.TransactionNotFound);
    }
    return undefined;
  };

  const fieldErrorMessage = getFieldErrorMessage();
  const fieldErrors: FieldErrors<TransactionIdEntryFormData> | undefined =
    fieldErrorMessage
      ? {
          transactionPublicID: {
            type: "validate",
            message: fieldErrorMessage,
          },
        }
      : undefined;

  const formTitle = t(
    isPhone ? PageResources.ManualTitleWithNfc : PageResources.ManualTitle,
  );

  const getStatusAdornment = () => {
    if (isLoadingTransaction) {
      return <CircularProgress size={20} />;
    }
    if (isInvalid) {
      return <CancelIcon color="error" fontSize="small" />;
    }
    if (hasExactLength && transactionDetails) {
      return <CheckCircleIcon color="success" fontSize="small" />;
    }
    return undefined;
  };

  const onSubmit = ({ transactionPublicID }: TransactionIdEntryFormData) => {
    const query = publicID ? `?publicID=${encodeURIComponent(publicID)}` : "";
    navigate(
      `${appUrl("customer-payment-pay")}/${transactionPublicID}${query}`,
    );
  };

  return (
    <Stack height="100%" justifyContent="flex-start">
      {isPhone && <NfcPrompt />}
      <PrimaryPaper>
        <TransactionIdEntryForm
          control={control}
          onSubmit={handleSubmit(onSubmit)}
          title={formTitle}
          errors={fieldErrors}
          inputEndAdornment={getStatusAdornment()}
          belowInput={
            statusAlertKind && (
              <TransactionStatusAlert status={statusAlertKind} />
            )
          }
          isSubmitDisabled={isSubmitDisabled}
          isSubmitLoading={isLoadingTransaction}
        />
      </PrimaryPaper>
    </Stack>
  );
};
